import { Injectable } from '@angular/core';
import { EnabledDynamicFieldModel } from '@core/api/models/dynamicfield/enabled-dynamicfield.model';
import { Organisation, OrganisationAdapter } from '@core/models/organisation/organisation.model';
import { BehaviorSubject, Subscription, combineLatest, forkJoin, Observable, of } from 'rxjs';
import { OrganisationProfile, OrganisationProfileAdapter} from '@core/models/organisation/organisation-profile';
import { Branch } from '@core/models/branch.model';
import { OrganisationMember, OrganisationMemberAdapter } from '@core/models/organisation/organisation-member';
import { CustomField, CustomFieldAdapter } from '@core/models/customField.model';
import { OrganisationProfileApiService } from '@core/api/services/organisation-profile-api.service';
import { OrganisationProfileSocialBalanceEntryApiService } from '@core/api/services/organisation-profile-social-balance-entry-api.service';
import { OrganisationProfileCertificationNodeApiService } from '@core/api/services/organisation-profile-certification-node-api.service';
import { CertificationNode, CertificationNodeAdapter } from '@modules/organisation-workspaces/models/certification-node';
import { GrantYearlyMaximumApiService } from '@core/api/services/grant-yearly-maximum-api.service';
import { GrantMaximumMatrix, GrantYearlyMaximum, GrantYearlyMaximumAdapter } from '@modules/organisation-workspaces/models/grant-yearly-maximum';
import { concatMap, map, shareReplay, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { OrganisationProfileMemberModel, OrganisationProfileModel } from '@core/api/models/organisation/organisation-profile';
import { OrganisationProfileMemberApiService } from '@core/api/services/organisation-profile-member-api.service';
import { OrganisationApiService } from '@core/api/services/organisation-api.service';
import { OrganisationModel } from '@core/api/models/organisation/organisation.model';
import { OrganisationAddressApiService } from '@core/api/services/organisation-address-api.service';
import { OrganisationProfileAddress, OrganisationProfileAddressAdapter } from '@core/models/organisation/organisation-profile-address';
import { OrganisationAddressModel } from '@core/api/models/organisation/organisation-address';
import { OrganisationBranchConnectionModel } from '@core/api/models/organisation/organisation.branch.connection.model';
import { OrganisationBranchConnectionApiService } from '@core/api/services/organisation-branch-connection-api.service';
import { OrganisationBranchConnectionCatalogRuleApiService } from '@core/api/services/organisation-branch-connection-catalog-rule-api.service';
import { ClientApiService } from '@core/api/services/client-api.service';
import { ClientModel } from '@core/api/models/client.model';
import { checkVAT, countries } from 'jsvat-be-fix';
import { PageableFactory } from '@core/pagination/pageable.factory';
import { Pageable } from '@core/pagination/pageable';
import { OrganisationAdmin, OrganisationAdminAdapter } from '@core/models/organisation/organisation-admin';
import { DepartmentAdapter } from '@core/models/organisation/department.model';
import { JointCommittee } from '@core/models/organisation/joint-committee.model';
import { CommitteeApiService } from '@core/api/services/committees-api.service';
import { DepartmentApiService } from '@core/api/services/department-api.service';
import { OrganisationProfileFunctionApiService } from '@core/api/services/organisation-profile-function-api.service';
import { OrganisationProfileFunction } from '@core/models/organisation/employment.model';
import { OrganisationProfileSubscriptionApiService } from '@core/api/services/organisation-profile-subscription.api.service';
import { OrganisationProfileCommitteeApiService } from '@core/api/services/organisation-profile-committee.api.service';
import { OrganisationJointCommittee } from '@core/models/organisation/organisation-joint-committee.model';
import { CourseTheme } from '@core/models/organisation/course-theme.model';
import { TrainingPlanCourseThemeApiService } from '@core/api/services/training-plan-course-theme-api.service';
import { OrganisationMemberListItem, OrganisationMemberListItemAdapter } from '@core/models/organisation/organisation-member-list-item.model';
import { TeamsApiService } from '@core/api/services/teams-api-service';
import { OrganisationTeam, OrganisationTeamAdapter } from '@core/models/organisation/organisation-team';
import { IntegrationApiService } from '@core/api/services/integration-api.service';
import { Integration, IntegrationDynamicFieldType, IntegrationFeature } from '@core/models/integration.model';
import { SubscriptionPackageApiService } from '@core/api/services/subscription-packages-api.service';
import { SubscriptionPackageAdapter, SubscriptionPackageCategory, SubscriptionPackageFeature, SubscriptionPackagePrice } from '@core/models/organisation/subscription-package.model';
import { FeatureApiService } from '@core/api/services/feature-api.service';
import { SubscriptionCategoryApiService } from '@core/api/services/subscription-category-api.service';
import { SubscriptionAddOn, SubscriptionAddOnQuote, adaptSubscriptionAddOn } from '@core/models/organisation/subscription-add-on.model';
import { OrganisationListItem, OrganisationListItemAdapter } from '@core/models/organisation/organisation-list-item.model';
import { OrganisationBranchConnectionListItemAdapter } from '@core/models/branchconnection.model';
import { BranchConnectionCatalogRule, BranchConnectionCatalogRuleAdapter } from '@core/models/branch-connection-catalog-rule.model';
import { AcademyCourse } from '@modules/organisation-workspaces/models/academy-course';
import { AcademyCourseAdapter } from '@modules/organisation-workspaces/adapters/academy-course.adapter';
import { AutocompleteOption } from '@shared/components/autocomplete-select/autocomplete.option';
import { FeatureService } from './feature.service';
import { OrganisationProfileAttachment, OrganisationProfileAttachmentAdapter } from '@core/models/organisation/organisation-profile-attachment';
import { OrganisationProfileAttachmentApiService } from '@core/api/services/organisation-profile-attachments-api.service';
import { OrganisationProfileWageCostApiService } from '@core/api/services/organisation-profile-wagecost.api.service copy';
import { OrganisationProfileBudgetApiService } from '@core/api/services/organisation-profile-budget.api.service';
import { WageCost } from '@modules/organisation-workspaces/components/wagecost-dialog/wagecost-model';
import { FileCategoryApiService } from '@core/api/services/file-category-api.service';
import { GrantsApiService } from '@core/api/services/grants-api-service';
import { IntegrationAdapter } from '@modules/organisation-workspaces/adapters/integration-adapter';
import { CertificationsApiService } from '@core/api/services/certifications-api.service';
import { HydraCollection } from '@core/api/hydra/hydra-collection';
import { Certification } from '@modules/organisation-workspaces/models/certification-matrix';
import { adaptQualificationCounts, qualificationCountTotal } from '@core/models/organisation/qualification.model';

@Injectable({
  providedIn: 'root'
})
export class OrganisationService {


  currentOrganisation$: BehaviorSubject<Organisation|null> = new BehaviorSubject<Organisation|null>(null);
  currentOrganisationTeams$: BehaviorSubject<OrganisationTeam[]|null> = new BehaviorSubject<OrganisationTeam[]|null>(null);
  currentOrganisationFunctions$: BehaviorSubject<OrganisationProfileFunction[]|null> = new BehaviorSubject<OrganisationProfileFunction[]|null>(null);
  currentOrganisationJointCommittees$: BehaviorSubject<JointCommittee[]|null> = new BehaviorSubject<JointCommittee[]|null>(null);
  currentOrganisationList$: BehaviorSubject<OrganisationListItem[]|null> = new BehaviorSubject<OrganisationListItem[]|null>(null);
  private organisationListSubscription?: Subscription;
  private currentOrg:Organisation|null = null;

  constructor(
    private organisationProfileApiService:OrganisationProfileApiService,
    private organisationProfileWageCostApiService:OrganisationProfileWageCostApiService,
    private organisationProfileBudgetApiService:OrganisationProfileBudgetApiService,
    private organisationApiService:OrganisationApiService,
    private organisationAdapter:OrganisationAdapter,
    private organisationListItemAdapter:OrganisationListItemAdapter,
    private organisationMemberAdapter:OrganisationMemberAdapter,
    private organisationMemberListItemAdapter:OrganisationMemberListItemAdapter,
    private organisationProfileAddressAdapter:OrganisationProfileAddressAdapter,
    private organisationProfileAdapter:OrganisationProfileAdapter,
    private organisationAddressApiService:OrganisationAddressApiService,
    private organisationBranchConnectionApiService:OrganisationBranchConnectionApiService,
    private organisationProfileCommitteeApiService:OrganisationProfileCommitteeApiService,
    private clientApiService:ClientApiService,
    private organisationProfileMemberApiService:OrganisationProfileMemberApiService,
    private committeeApiService:CommitteeApiService,
    private organisationAdminAdapter:OrganisationAdminAdapter,
    private departmentAdapter:DepartmentAdapter,
    private departmentApiService:DepartmentApiService,
    private organisationProfileFunctionApiService:OrganisationProfileFunctionApiService,
    private organisationProfileSubscriptionApiService: OrganisationProfileSubscriptionApiService,
    private courseThemeApiService:TrainingPlanCourseThemeApiService,
    private teamApiService:TeamsApiService,
    private integrationApiService:IntegrationApiService,
    private organisationTeamAdapter:OrganisationTeamAdapter,
    private subscriptionPageApiService:SubscriptionPackageApiService,
    private featureApiService:FeatureApiService,
    private subscriptionCategoryApiService:SubscriptionCategoryApiService,
    private subscriptionPackageAdapter:SubscriptionPackageAdapter,
    private organisationBranchConnectionListItemAdapter:OrganisationBranchConnectionListItemAdapter,
    private organisationBranchConnectionCatalogRuleApiService:OrganisationBranchConnectionCatalogRuleApiService,
    private branchConnectionCatalogRuleAdapter:BranchConnectionCatalogRuleAdapter,
    private academyCourseAdapter:AcademyCourseAdapter,
    private featureService:FeatureService,
    private organisationProfileAttachmentApiService:OrganisationProfileAttachmentApiService,
    private organisationProfileAttachmentAdapter:OrganisationProfileAttachmentAdapter,
    private fileCategoryApiService:FileCategoryApiService,
    private grantsApiService:GrantsApiService,
    private integrationAdapter:IntegrationAdapter,
    private certificationApiService:CertificationsApiService,
    private customFieldAdapter: CustomFieldAdapter,
    private socialBalanceEntryApiService:OrganisationProfileSocialBalanceEntryApiService,
    private certificationNodeApiService:OrganisationProfileCertificationNodeApiService,
    private certificationNodeAdapter:CertificationNodeAdapter,
    private grantYearlyMaximumApiService:GrantYearlyMaximumApiService,
    private grantYearlyMaximumAdapter:GrantYearlyMaximumAdapter,
    private router:Router){
      this.getJointCommittees()
    }



  getAll() {
    this.organisationListSubscription?.unsubscribe();
    this.organisationListSubscription = this.organisationProfileApiService.list().subscribe((x:any) => {
        const members = x.members.map((m:any) => this.organisationListItemAdapter.adapt(m)).sort((a:OrganisationListItem, b:OrganisationListItem) => {

          if (a.isPremium !== b.isPremium) {
            return a.isPremium === true ? -1 : 1;
          }

          const aIsManagerOrAdmin = a.manager || a.admin;
          const bIsManagerOrAdmin = b.manager || b.admin;
          if (aIsManagerOrAdmin !== bIsManagerOrAdmin) {
            return aIsManagerOrAdmin === true ? -1 : 1;
          }
          return a.name.localeCompare(b.name);
        });
        this.currentOrganisationList$.next(members);
      });
  }

  search(keyword:string, requireVat:boolean = false):Observable<Organisation[]> {
    return this.organisationProfileApiService.search(keyword, requireVat).pipe(
      switchMap(
      (x:any) =>
        {
          return of(x.members.map((m:any) => this.organisationAdapter.adaptForLookup(m)));
        }
      )
    );
  }

  checkConnection(id:string):Observable<boolean>{
    return this.organisationProfileMemberApiService.checkConnection(id).pipe(
      switchMap(
        (x:any) =>
        {
          return of(x.totalItems)
        }
      )
    );
  }


  getById(id:string):Observable<Organisation>{
    return forkJoin({
      org: this.organisationProfileApiService.get(id),
      departments: this.organisationProfileApiService.getDepartments(id, {'order[name]':'asc'})
    }).pipe(map(({org, departments}) =>{
      const adapted = this.organisationAdapter.adapt(org);
      adapted.departments = (departments.members ?? []).map((d:any) => this.departmentAdapter.adapt(d));
      return adapted;
    }));
  }

  getAdmins(id:string){
    return this.organisationProfileApiService.getAdmins(id).pipe(
      concatMap(async collection =>
        {
          return new Pageable<OrganisationAdmin>(
            collection.items.map( (x:any) => this.organisationAdminAdapter.adapt(x)),
            collection.pagination.currentPage,
            collection.pagination.totalPages,
            collection.pagination.totalItems,
            collection.pagination.itemsPerPage,
            collection.pagination.view
          )
        }
      ));
  }


  createDepartment(body:any, organisationId: string = this.currentOrg?.id ?? '', refresh = true){

    const data = {
      name: body.name,
      address: body.address,
      emailAddress: body.emailAddress,
      contactPerson: body.contact,
      phoneNumber: body.phoneNumber,
      organisationProfile: "/organisation_profiles/" + organisationId
    }

    return this.departmentApiService.create(data).pipe(switchMap(
      (result) =>{
        const department = this.departmentAdapter.adapt(result);
        if (refresh && this.currentOrg?.id === organisationId) this.refreshOrganisation(organisationId);
        return of(department)
      }
    ))
  }

  updateDepartment(id:string, body:any){

    const data = {
      name: body.name,
      address: body.address,
      emailAddress: body.emailAddress,
      contactPerson: body.contact,
      phoneNumber: body.phoneNumber
    }

    return this.departmentApiService.update(id,data).pipe(switchMap(
      (result) =>{
        const department = this.departmentAdapter.adapt(result);
        const dep = this.currentOrg?.departments.find(x => x.id == id);
        if(dep){
          Object.assign(dep, department);
        }
        this.setCurrentOrganisation(this.currentOrg);
        return of(department)
      }
    ))
  }

  deleteDepartment(id:string){

    return this.departmentApiService.delete(id).pipe(switchMap(
      () => {
        if(this.currentOrg){
        this.refreshOrganisation(this.currentOrg.id);
        }
        return of(true);
      }
    ));
  }

  createFunction(body:any){
    const data = {
      ...body,
      organisationProfile: "/organisation_profiles/" + this.currentOrg?.id,
    };
    return this.organisationProfileFunctionApiService.create(data).pipe(switchMap((result:any) =>{
      if(this.currentOrg) this.getFunctions(this.currentOrg.id);
      return of({ id: result.id, name: result.name, certifications: result.defaultCertifications ?? [] } as OrganisationProfileFunction);
    }));
  }

  updateFunction(id:string, body:any){
    return this.organisationProfileFunctionApiService.update(id, body).pipe(switchMap((result:any) =>{
      const updated = { id: result.id, name: result.name, certifications: result.defaultCertifications ?? [] } as OrganisationProfileFunction;
      if(this.currentOrg) this.getFunctions(this.currentOrg.id);
      return of(updated);
    }));
  }

  deleteFunction(id:string){
    return this.organisationProfileFunctionApiService.delete(id).pipe(switchMap(() =>{
      if(this.currentOrg) this.getFunctions(this.currentOrg.id);
      return of(true);
    }));
  }

  getFunctions(id:string){
    this.organisationProfileApiService.getFunctions(id, {'order[name]':'asc'}).subscribe(collection =>{
      const functions = (collection.members ?? []).map((f:any) => {
        const qualificationCounts = adaptQualificationCounts(f.qualificationCounts);
        return {
          id: f.id,
          name: f.name,
          certifications: (f.defaultCertifications ?? []).map((c:any) => ({ id: c.id, name: c.name })),
          memberCount:f.memberCount??0,
          qualificationCounts,
          qualificationCount:qualificationCountTotal(qualificationCounts)
        } as OrganisationProfileFunction;
      });
      this.currentOrganisationFunctions$.next(functions);
    });
  }

  update(id:string, body:any):Observable<Organisation>{
    return this.organisationProfileApiService.update(id,body).pipe(
      switchMap(() => this.getById(id)),
      switchMap(org => {
        this.setCurrentOrganisation(org);
        this.getAll();
        return of(org);
      })
    );
  }

  leave(id:string):Observable<boolean>{
    return this.organisationProfileMemberApiService.delete(id).pipe(
      switchMap(
        (x:any) =>{
          return of(true);
        }
      ));
  }

  createOrgOnly(body:object){
    return this.organisationProfileApiService.create(body).pipe(
      switchMap(x => {
        return of(this.organisationAdapter.adapt(x))
      }))
  }

  create(organisation:Organisation, branches:Branch[]):Observable<any>{
    //create orgprofile and orgs per client
    let clients:string[] = [];
    branches.forEach(branch => {
       if(!clients.some(y => y == branch.clientId)){
        clients.push(branch.clientId);
       }
    });
    const profilebody = {
      name: organisation.name,
      vatNumber:organisation.vatNumber != ""?checkVAT(organisation.vatNumber,countries).value:null,
      isoCountry:organisation.country??'BE'
    }

    return this.organisationProfileApiService.create(profilebody).pipe(
      switchMap(
        (x:OrganisationProfileModel) =>{

          organisation.id = x.id;
          let orgcalls:any = [];
          clients.forEach(item => {

            const orgbody = {
              client:"clients/"+item,
              organisationProfile:"organisation_profiles/"+ x.id,
              name:organisation.name,
              vatNumber:organisation.vatNumber??null,
              isoCountry:organisation.country??'BE'
            }
            orgcalls.push(this.organisationApiService.create(orgbody));

          });
         if(orgcalls.length){
            return forkJoin(orgcalls).pipe(switchMap(
              (organisations:any) =>{
                let branchcalls:any = [];

                organisations.forEach((org:OrganisationModel) => {
                  organisation.profiles.push(this.organisationProfileAdapter.adapt(org));
                  branches.filter(y => y.clientId == org.client.id).forEach(b =>{
                    const cbody = {
                      organisation:'organisations/'+org.id,
                      branch:'branches/'+ b.id,
                      active:b.active
                    }
                    branchcalls.push(this.organisationBranchConnectionApiService.create(cbody));
                  })

                })

                return forkJoin(branchcalls).pipe(switchMap(
                  (x:any)=>{
                    return of(organisation);
                  }
                ));
              }
            ));
         }else{
           return of(organisation);
         };

        }
      )
    )
  }

  delete(id:string):Observable<boolean>{
    return of(true);
  }

  getMembers(id:string, criteria: any = null , page: number = 1):Observable<Pageable<OrganisationMemberListItem>>{

    const params: Record<string, string> = {};
    if(criteria){
      params['page'] = page.toString();

      if (criteria.search) {
        params['search'] = criteria.search;
      }

      if (criteria.productId) {
        params['productId'] = criteria.productId;
      }

      if (criteria.department) {
        const departments = Array.isArray(criteria.department) ? criteria.department : [criteria.department];
        departments.forEach((d:string,i:number) => {
          params['employments.department['+i+']'] = '/organisation_profile_departments/'+d;
        });
      }
      if (criteria.committee) {
        const committees = Array.isArray(criteria.committee) ? criteria.committee : [criteria.committee];
        committees.forEach((c:string,i:number) => {
          params['employments.jointCommittee.jointCommittee['+i+']'] = '/joint_committees/'+c;
        });
      }
      if (criteria.function) {
        const functions = Array.isArray(criteria.function) ? criteria.function : [criteria.function];
        functions.forEach((f:string,i:number) => {
          params['employments.function['+i+']'] = '/organisation_profile_functions/'+f;
        });
      }

      if (criteria.status && criteria.status.length) {
        if(Array.isArray(criteria.status)){
          criteria.status.forEach((status:string,i:number) => {
            params['status['+i+']'] = status;
          });
        }else{
          params['status'] = criteria.status;
        }

      }
      if (criteria.team && criteria.team.length) {
        const teams = Array.isArray(criteria.team) ? criteria.team : [criteria.team];
        const realTeams = teams.filter((t:string) => t !== '0');
        if(teams.includes('0')){
          params['noTeam'] = '1';
        }
        realTeams.forEach((t:string,i:number) => {
          params['teams['+i+']'] = '/organisation_profile_teams/'+t;
        });
      }
      if (criteria.paging) {
        params['paging'] = criteria.paging;
      }
      if (criteria.userVerified !== undefined) {
        params['userVerified'] = String(criteria.userVerified);
      }
      if (criteria.admin !== undefined) {
        params['admin'] = String(criteria.admin);
      }
    }

    if(criteria?.listMode){
      params['listMode'] = 'true';
    }else{
      params['view'] = "organisation_profile_member_minified_list";
    }
    params['order[userProfile.lastName]'] = 'asc';
    return this.organisationProfileApiService.getMembers(id, params).pipe(
      concatMap(async collection => PageableFactory.createFromCollectionStatic<OrganisationMemberListItem>(collection, this.organisationMemberListItemAdapter))
    );

  }

  getMemberOptions(id: string, criteria: any = null): Observable<AutocompleteOption[]> {
    const params: Record<string, string> = { listMode: 'true', 'order[userProfile.lastName]': 'asc' };
    if (criteria?.search) params['search'] = criteria.search;

    return this.organisationProfileApiService.getMembers(id, params).pipe(
      map(collection => collection.items.map((m: any) => ({
        label: m.userProfile.firstName + ' ' + m.userProfile.lastName + (m.userProfile.emailAddress ? '<br><small>' + m.userProfile.emailAddress + '</small>' : ''),
        avatarLabel: m.userProfile.firstName + ' ' + m.userProfile.lastName,
        value: m.id,
        emitValue: m
      } as AutocompleteOption)))
    );
  }

  updateMember(id:string,body:any):Observable<OrganisationMember>{
    return this.organisationProfileMemberApiService.update(id,body).pipe(
      switchMap(
        (x:OrganisationProfileMemberModel) =>{
          return of(this.organisationMemberAdapter.adapt(x));
        }
      )
    )
  }

  updateBranchStatus(branch:Branch):Observable<Branch>{

    branch.active = !branch.active;
    const cbody = {
      active:branch.active
    }

    return this.organisationBranchConnectionApiService.update(branch.organisationConnectionId, cbody).pipe(
      switchMap(
        (x:OrganisationBranchConnectionModel) =>{
          return of(branch);
        }
      )
    );

    }

  getProfile(id:string, fields:CustomField[]):Observable<OrganisationProfile>{
    return forkJoin({profile:this.organisationApiService.get(id),addressbook:this.organisationApiService.getAddresses(id)}).pipe(
      switchMap(
      (x:any) =>
        {
          let profile:OrganisationProfile = this.organisationProfileAdapter.adapt(x.profile);

          if(x.addressbook.totalItems){
            profile.addressBook = x.addressbook.members.map((a:OrganisationAddressModel) => this.organisationProfileAddressAdapter.adapt(a));
          }

          profile.fields = fields.slice();
          profile.fields.forEach(field => {
            let valueField = x.profile.dynamicFieldValues.find((z:any) => z.dynamicField.id == field.id);
            if(valueField){
              field.value = valueField.value;
            }
          });
          return of(profile);
        }




      ));
  }

  getProfiles(orgid:string):Observable<Array<OrganisationProfile>>{
    return this.organisationProfileApiService.getOrganisations(orgid).pipe(
      switchMap(
        (x:any) =>{
          return of(x.members.map((o:OrganisationModel) => this.organisationProfileAdapter.adapt(o)));
        }
      )
    )
  }

  createProfile(organisation:string,clientId:string,branches:Branch[] = []):Observable<OrganisationProfile>{
    const orgbody = {
      client:"clients/"+clientId,
      organisationProfile:"organisation_profiles/"+ organisation,
      name:'temp',
      vatNumber:null
    }
    return this.organisationApiService.create(orgbody).pipe(
      switchMap(
        (x:OrganisationModel) =>{

        return this.clientApiService.get(clientId).pipe(
            switchMap(
            (client:ClientModel) =>{


              let branchcalls:any = [];
              if(branches.length){
                branches.forEach(b => {
                  const cbody = {
                    organisation:'organisations/'+x.id,
                    branch:'branches/'+ b.id,
                    active:true
                  }
                  branchcalls.push(this.organisationBranchConnectionApiService.create(cbody));

                });
              }else{
                client.branches.forEach(b => {
                  const cbody = {
                    organisation:'organisations/'+x.id,
                    branch: b,
                    active: false
                  }
                  branchcalls.push(this.organisationBranchConnectionApiService.create(cbody));

                });
              }

              return forkJoin(branchcalls).pipe(
                switchMap(
                (branchconnections:any) =>{
                  return of(this.organisationProfileAdapter.adapt(x));
                }
              ))
            }
            )
          )
        }
      )
    );
  }

  saveProfile(id:string, body:any):Observable<OrganisationProfile>{
    return this.organisationApiService.update(id,body).pipe(
      switchMap(
      (x:any) =>
        {
          return of(this.organisationProfileAdapter.adapt(x));
        }
      ));
  }

  connectToOrganisation(organisationProfile:string, userProfile:string):Observable<OrganisationMember>{
      const body = {
        organisationProfile: 'organisation_profiles/'+ organisationProfile,
        userProfile:'user_profiles/'+ userProfile,
        admin:false,
        role:""
      }

      return this.organisationProfileMemberApiService.create(body).pipe(
        switchMap(
          (x:OrganisationProfileMemberModel) => {return of(this.organisationMemberAdapter.adapt(x))}
        )
      );

  }

  saveAddress(id:string, body:any):Observable<OrganisationProfileAddress>{
    const addressbody = {
        organisation: "organisations/"+ id,
        name: body.name,
        emailAddress: body.email,
        address: {
          address: body.address.address??'',
          postcode: body.address.postcode,
          city: body.address.city,
          country: body.address.country
        },
        phoneNumber: body.phoneNumber
      }
    const call = body.id? this.organisationAddressApiService.update(body.id,addressbody): this.organisationAddressApiService.create(addressbody)
    return call.pipe(switchMap(
      (x:OrganisationAddressModel) =>{
        return of(this.organisationProfileAddressAdapter.adapt(x));
      }
    ))
  }

  deleteAddress(id:string){
    return this.organisationAddressApiService.delete(id).pipe(switchMap((x:any) => {return of(true)}));
  }

  getOrganisationAddressesByClientID(orgId:string,clientId:string):Observable<Array<OrganisationProfileAddress>>{
    return this.organisationAddressApiService.getAllByClient(orgId,clientId).pipe(
      switchMap(
        (x:any) => {
          return of(x.members.map((a:OrganisationAddressModel) => this.organisationProfileAddressAdapter.adapt(a)));
        }
      )
    )
  }

  getCustomFields(clientId:string):Observable<CustomField[]>{
    return this.clientApiService.getCustomFieldsForOrganisation(clientId).pipe(
        switchMap((fields:any) =>{
          let cFields:CustomField[] = [];
          if(fields.totalItems){
            cFields = fields.members.map((m:EnabledDynamicFieldModel) => {let df:CustomField = this.customFieldAdapter.adapt(m);df.required = m.requiredForOrganisations; return df;})
          }
            return of(cFields);
          }
        )
      )
}

// Worldwide (WW) organisations use worker types (white/blue/pink collar) instead of Belgian PCs.
jointCommitteesCountry = '';
getJointCommittees(country = 'BE'){
  const isoCountry = country == 'WW' ? 'WW' : 'BE';
  if(isoCountry == this.jointCommitteesCountry) return;
  this.jointCommitteesCountry = isoCountry;
  const params: Record<string, string> = {};
  params['order[number]'] = 'asc';
  params['isoCountry'] = isoCountry;
  this.committeeApiService.list(params).pipe(shareReplay()).subscribe((x) =>{
    if(isoCountry != this.jointCommitteesCountry) return;
    this.currentOrganisationJointCommittees$.next(x.members.map(c =>  {return { id: c.id, name: isoCountry == 'WW' ? c.name : c.number, description: c.description, wageCosts:[]} as JointCommittee}));
  })
}

addOrgCommittee(orgId:string, committeeId:string, hours:number):Observable<OrganisationJointCommittee>{
  return this.organisationProfileCommitteeApiService.create(
    {
      "organisationProfile": '/organisation_profiles/'+orgId,
      "jointCommittee": '/joint_committees/'+committeeId,
      "workingHoursPerWeek": hours
    }
  ).pipe(map(x => {return {
    id:x.id,
    organisationProfile:x.organisationProfile.split('/').pop(),
    jointCommittee:x.jointCommittee.split('/').pop(),
    workingHoursPerWeek: x.workingHoursPerWeek??0,
    archived:x.archived,
    amountOfActiveEmployees: x.amountOfActiveEmployees,
    amountOfFullTimeEquivalentEmployees: x.amountOfFullTimeEquivalentEmployees
  }}))
}
updateOrgCommittee(id:string, orgId:string, committeeId:string, hours:number, archived:boolean):Observable<OrganisationJointCommittee>{
  return this.organisationProfileCommitteeApiService.update(id,
    {
      "organisationProfile": '/organisation_profiles/'+orgId,
      "jointCommittee": '/joint_committees/'+committeeId,
      "workingHoursPerWeek": hours,
      "archived":archived
    }
    ).pipe(map(x => {return {
      id:x.id,
      organisationProfile:x.organisationProfile.split('/').pop(),
      jointCommittee:x.jointCommittee.split('/').pop(),
      workingHoursPerWeek: x.workingHoursPerWeek??0,
      archived:x.archived,
      amountOfActiveEmployees: x.amountOfActiveEmployees,
      amountOfFullTimeEquivalentEmployees: x.amountOfFullTimeEquivalentEmployees
    }}))
}

setCurrentOrganisation(org:Organisation|null){
    this.currentOrg = org;
    this.getJointCommittees(org?.country);
    if(org){
      this.featureService.getFeatures(org);
    }else{
      this.featureService.clear();
    }
    this.currentOrganisation$.next(org);
}

validateMembers(data:any):Observable<any[]>{
  return this.organisationProfileApiService.validateMembers(this.currentOrg?.id??'', data)
}

importMembers(data:any):Observable<boolean>{
  return this.organisationProfileApiService.importMembers(this.currentOrg?.id??'', data);
}

validateImportAttendees(data:any):Observable<any[]>{
  return this.organisationProfileApiService.validateImportAttendees(this.currentOrg?.id??'', data)
}

importAttendees(data:any):Observable<any>{
  return this.organisationProfileApiService.importAttendees(this.currentOrg?.id??'', data)
}

addReferral(id:string){
  return this.organisationProfileSubscriptionApiService.addReferral(id);
}

getThemes(id:string){
  return this.courseThemeApiService.list(id).pipe(switchMap((x) =>{
    return of(x.members.map(c =>  { return { id: c.id, name: c.name, usableInTrainingPlan: c.usableInTrainingPlan, visibleForEmployees:c.visibleForEmployees} as CourseTheme}));
  }))
}

createTheme(body:object){
  return this.courseThemeApiService.create(body).pipe(map((result:any) =>{
    return {id:result.id, name:result.name, usableInTrainingPlan: result.usableInTrainingPlan, visibleForEmployees:result.visibleForEmployees} as CourseTheme
  }))
}

updateTheme(id:string,body:object){
  return this.courseThemeApiService.update(id,body).pipe(map((result:any) =>{
    return {id:result.id, name:result.name, usableInTrainingPlan: result.usableInTrainingPlan, visibleForEmployees:result.visibleForEmployees} as CourseTheme
  }))
}

deleteTheme(id:string){
  return this.courseThemeApiService.delete(id);
}

cancelSubscription(id:string){
  return this.organisationProfileSubscriptionApiService.disableAutoRenewal(id);
}

activateSubscription(id:string){
  return this.organisationProfileSubscriptionApiService.enableAutoRenewal(id);
}

getTeams(id:string){
  this.teamApiService.list(id).subscribe((x) =>{
    const teams = x.members.map((c:any) =>  {
      const qualificationCounts = adaptQualificationCounts(c.qualificationCounts);
      return { id: c.id, name: c.name,  memberCount:c.memberCount, qualificationCounts, qualificationCount:qualificationCountTotal(qualificationCounts), budgetCount:c.budgetCount ?? 0} as OrganisationTeam;
    });
    this.currentOrganisationTeams$.next(teams);
  })
}

getTeam(id:string){
  return this.teamApiService.get(id).pipe(map((c:any) =>{
    const qualificationCounts = adaptQualificationCounts(c.qualificationCounts);
    return { id: c.id, name: c.name, memberCount: c.memberCount, qualificationCounts, qualificationCount: qualificationCountTotal(qualificationCounts), budgetCount: c.budgetCount ?? 0} as OrganisationTeam;
  }))
}

updateTeam(id:string, data:object){
  return this.teamApiService.update(id,data).pipe(map(t => {const team = this.organisationTeamAdapter.adapt(t);return team}));
}

createTeam(data:object){
  return this.teamApiService.create(data).pipe(map(t => {const team = this.organisationTeamAdapter.adapt(t); return team}));
}

deleteTeam(id:string){
  return this.teamApiService.delete(id);
}

private orgSubscription: Subscription | undefined;
isSwitchingToOrg = false;

switchToOrg(id: string){
  if (this.orgSubscription) {
    this.orgSubscription.unsubscribe();
  }
  this.isSwitchingToOrg = true;
  this.clear();
  this.orgSubscription = this.getById(id).subscribe(org => {
    this.setCurrentOrganisation(org);
  }, error => {
    this.isSwitchingToOrg = false;
    if(error.status == 403){
      this.router.navigate(['/dashboard']);
    }
  });
}

deactivateOrg(){
  if (this.orgSubscription) {
    this.orgSubscription.unsubscribe();
  }
  this.isSwitchingToOrg = false;
  this.clear();
}

clear(){
  this.currentOrg = null;
  this.currentOrganisation$.next(null);
  this.currentOrganisationTeams$.next(null);
  this.currentOrganisationFunctions$.next(null);
  this.featureService.clear();
}

clearOrgList(){
  this.organisationListSubscription?.unsubscribe();
  this.currentOrganisationList$.next(null)
}

uploadImage(file:File){
  var formData: any = new FormData();
  formData.append('file', file);
  return this.organisationApiService.uploadFile(formData);
}


getDashboard(orgId:string,criteria: any = null , page: number = 1){
  const params: Record<string, string> = {};
  if(criteria){
    params['page'] = page.toString();

    if (criteria.search && criteria.search.length) {
      params['search'] = criteria.search;
    }
    if (criteria.year) {
      params['year'] = criteria.year;
    }
    if (criteria.department) {
      params['department'] =  criteria.department;
    }
    if (criteria.committee) {
      params['jointCommittee'] = criteria.committee;
    }
    if (criteria.team) {
      params['team'] =  criteria.team;
    }
    if (criteria.view) {
      params['view'] =  criteria.view;
    }
  }
  return this.organisationProfileApiService.getDashboard(orgId,params);
}

getTrainingRightCompliance(orgId:string, criteria?: any){
  const params: Record<string, string> = {};
  params['order[total]']= 'desc';
  if(criteria){
    if (criteria.year) {
      params['year'] = criteria.year;
    }
    if (criteria.compliance) {
      params['compliance'] = criteria.compliance;
    }
  }
  return this.organisationProfileApiService.getTrainingRightCompliance(orgId, params);
}

getIntegrations(orgId:string){
  return this.integrationApiService.list({'organisationProfile.id':orgId}).pipe(map(result => {
    return result.members.map(x => this.integrationAdapter.adapt(x));
  }));
}

refreshOrganisation(id:string){
  this.getById(id).subscribe(org =>{
    this.setCurrentOrganisation(org);
  })
}

// ----- Add-ons: modules bought on top of the package -----

getSubscriptionAddOns(organisationId:string):Observable<SubscriptionAddOn[]>{
  // The API also lists what the package already includes; there is nothing to sell there.
  return this.organisationProfileApiService.listSubscriptionAddOns(organisationId).pipe(map(result => (result?.addOns ?? []).filter(a => !a.includedInPackage).map(adaptSubscriptionAddOn)));
}

/** With `dryRun`, what buying it would bill; without, bought. */
activateSubscriptionAddOn(organisationId:string, addOnId:string, dryRun:boolean):Observable<SubscriptionAddOnQuote>{
  return this.organisationProfileApiService.activateSubscriptionAddOn(organisationId, addOnId, dryRun);
}

startSubscriptionAddOnTrial(organisationId:string, addOnId:string):Observable<{ startDate:string, endDate:string }>{
  return this.organisationProfileApiService.trialSubscriptionAddOn(organisationId, addOnId);
}

/** Stopped at the end of the running period; the answer is the last day it is still active. */
deactivateSubscriptionAddOn(organisationId:string, addOnId:string):Observable<{ endDate:string }>{
  return this.organisationProfileApiService.deactivateSubscriptionAddOn(organisationId, addOnId);
}

getAllSubscriptionPackages(organisationId:string){

  return combineLatest({categories:this.subscriptionCategoryApiService.list({'order[position]':'asc'}),features:this.featureApiService.list({'order[position]':'asc'}),packages: this.subscriptionPageApiService.list({'order[position]':'asc', 'organisation':organisationId}) })
  .pipe(map(result =>{
    const categories = result.categories.members.map(x => {return {id: x.id, name:x.name, min: x.minEmployees, max: x.maxEmployees, position: x.position, prices :
      x.prices.map((p:any) => {return { id:p.id, price: p.monthlyPrice??0 , name: p.package.name, onRequest : p.monthlyPrice === null  } as SubscriptionPackagePrice})
     } as SubscriptionPackageCategory})

    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    const features = result.features.members.map(x => { const inputDate = new Date(x.createdAt); return {id: x.id, name: x.translations??x.translationKey, systemKey: x.translationKey, new:inputDate > threeMonthsAgo, category: (x.category ?{id: x.category.id, name: x.category.name}:null), description: x.descriptionTranslation } as SubscriptionPackageFeature})
    return {categories: categories, packages:result.packages.members.map(x => this.subscriptionPackageAdapter.adapt(x)), features:features};
  }))
}

startTrial(orgid:string){
  return this.organisationProfileApiService.updateSubscription(orgid,{subId:null})
}

startTrialViaOnBoarding(orgid:string, data:object){
  return this.organisationProfileApiService.startTrialViaOnBoarding(orgid,data)
}
updateSubscription(orgid:string, data:object){
  return this.organisationProfileApiService.updateSubscription(orgid,data)
}

checkUpdateSubscription(orgid:string, data:object){
  return this.organisationProfileApiService.checkUpdateSubscription(orgid,data)
}

getAcademyConnections(orgid:string, activeOnly:boolean = false){
  return this.organisationProfileApiService.getAcademyConnections(orgid, activeOnly).pipe(map((result:any) => {return result.map((x:any) => this.organisationBranchConnectionListItemAdapter.adapt(x))}))
}

getCatalogRules(connectionId:string):Observable<BranchConnectionCatalogRule[]>{
  return this.organisationBranchConnectionCatalogRuleApiService.listByConnection(connectionId).pipe(map(rules => rules.map((x:any) => this.branchConnectionCatalogRuleAdapter.adapt(x))))
}

createCatalogRule(data:object):Observable<BranchConnectionCatalogRule|null>{
  return this.organisationBranchConnectionCatalogRuleApiService.create(data).pipe(map((x:any) => x ? this.branchConnectionCatalogRuleAdapter.adapt(x) : null))
}

updateCatalogRule(id:string, data:object):Observable<BranchConnectionCatalogRule|null>{
  return this.organisationBranchConnectionCatalogRuleApiService.update(id,data).pipe(map((x:any) => x ? this.branchConnectionCatalogRuleAdapter.adapt(x) : null))
}

deleteCatalogRule(id:string){
  return this.organisationBranchConnectionCatalogRuleApiService.remove(id)
}

saveAcademyConnections(orgid:string,data:object){
  return this.organisationProfileApiService.saveAcademyConnections(orgid,data)
}

getAcademyCourses(orgid:string, criteria:any,page:number = 1){
  const params: any = {};
  params['page'] = page.toString();
  params['itemsPerPage'] = '100';
  if(criteria){
    if (criteria.search) {
      params['search'] = criteria.search;
    }
    if (criteria.type) {
      params['type'] = criteria.type;
    }

    if(criteria.branch){
      params['branch'] = criteria.branch;
    }
    if(criteria.year){
      params['year'] = criteria.year;
    }

    if(criteria.maxPrice){
        params['maxPrice'] = parseFloat(criteria.maxPrice)
    }
  }
  return this.organisationProfileApiService.getAcademyCourses(orgid,params).pipe(map((x:any) =>{
    return new Pageable<AcademyCourse>(x.items.map((item:any) => this.academyCourseAdapter.adapt(item)),x.pagination.currentPage, x.pagination.totalPages ,x.pagination.totalItems,x.pagination.itemsPerPage)
  }));
}

importAcademyCourses(orgid:string,data:object){
  return this.organisationProfileApiService.importAcademyCourses(orgid,data)
}

getAttachments(orgid:string, criteria:any,page:number = 1){
  const params: any = {};
  params['page'] = page.toString();
  params['uploader.organisationProfile.id'] = orgid;
  if(criteria){
    if (criteria.search) {
      params['search'] = criteria.search;
    }
    if (criteria.sort) {
      const [key, value] = criteria.sort.split('=');
      if(key == 'order[member]'){
        params['order[member.userProfile.lastName]'] = value;
        params['order[member.userProfile.firstName]'] = value;
      }else{
        params[key] = value;
      }
    }
  }
  return this.organisationProfileAttachmentApiService.list(params).pipe(map(collection => PageableFactory.createFromHydraCollection<OrganisationProfileAttachment>(collection, this.organisationProfileAttachmentAdapter)));
}


getAttachmentsForCourse(courseId:string){
  return this.organisationProfileAttachmentApiService.list({'course.id':courseId}).pipe(map(collection => PageableFactory.createFromHydraCollection<OrganisationProfileAttachment>(collection, this.organisationProfileAttachmentAdapter)));
}

getAttachmentsForMember(memberId:string){
  return this.organisationProfileAttachmentApiService.list({'member.id':memberId}).pipe(map(collection => PageableFactory.createFromHydraCollection<OrganisationProfileAttachment>(collection, this.organisationProfileAttachmentAdapter)));
}


createAttachment(body:any){
  return this.organisationProfileAttachmentApiService.create(body).pipe(map(result => this.organisationProfileAttachmentAdapter.adapt(result)))
}
updateAttachment(id:string, body:any){
  return this.organisationProfileAttachmentApiService.update(id, body).pipe(map(result => this.organisationProfileAttachmentAdapter.adapt(result)))
}

deleteAttachment(id:string){
  return this.organisationProfileAttachmentApiService.delete(id);
}

getWageCosts(orgId:string):Observable<WageCost[]>{
  const params = {
    ['organisationProfile.id']: orgId,
    ['paging']:'false'
  }
  return this.organisationProfileWageCostApiService.list(params).pipe(map((result:any) =>{
    return result.members.map((x:any) =>{return { amount: x.amount??0, startDate: x.date??null, endDate:x.endDate??null, jointCommiteeId: (x.jointCommittee.jointCommittee??'').split('/').pop() } as WageCost})
  }))
}

getBudgetSettings(orgId:string):Observable<any[]>{
  const params = {
    ['organisationProfile.id']: orgId,
    ['paging']:'false'
  }
  return this.organisationProfileBudgetApiService.list(params).pipe(map((result:any) =>{
    return result.members.map((x:any) =>{return { amount: x.amount??0, startDate: x.startDate??null, endDate:x.endDate??null }})
  }))
}

// ----- Team budgets -----
// A team budget is an organisation_profile_budget that carries an organisationProfileTeam IRI.

getTeamBudgets(teamId:string):Observable<{id:string, amount:number, startDate:Date, endDate:Date}[]>{
  return this.organisationProfileBudgetApiService.listByTeam(teamId).pipe(map((result:any) =>
    result.members.map((x:any) => ({ id: x.id, amount: x.amount??0, startDate: x.startDate??null, endDate: x.endDate??null }))
  ));
}

createTeamBudget(orgId:string, teamId:string, body:{amount:number, startDate:string|null, endDate:string|null}){
  return this.organisationProfileBudgetApiService.create({
    organisationProfile: '/organisation_profiles/' + orgId,
    organisationProfileTeam: '/organisation_profile_teams/' + teamId,
    amount: body.amount,
    startDate: body.startDate,
    endDate: body.endDate,
  });
}

updateTeamBudget(id:string, body:{amount:number, startDate:string|null, endDate:string|null}){
  return this.organisationProfileBudgetApiService.update(id, body);
}

deleteTeamBudget(id:string){
  return this.organisationProfileBudgetApiService.delete(id);
}



getFileTypes(){
  return this.fileCategoryApiService.list({'isForMe':'1'}).pipe(map(x  => x.members))
}

updateFile(id:string,data:any){
  return this.organisationApiService.updateFile(id,data);
}

getGrantStats(id:string,year:number){
  return this.grantsApiService.statistics(id,year);
}

getSocialBalance(orgId: string, startDate: string, endDate: string): Observable<any> {
  return this.organisationProfileApiService.getSocialBalance(orgId, { startDate, endDate });
}

exportSocialBalance(orgId: string, startDate: string, endDate: string): Observable<any> {
  return this.organisationProfileApiService.exportSocialBalance(orgId, { startDate, endDate });
}

// ----- Certification catalogue -----

// The whole graph in one unpaginated call; both edge directions come down as IRI arrays.
getCertificationNodes(orgId: string): Observable<CertificationNode[]> {
  return this.organisationProfileApiService.getCertificationNodes(orgId).pipe(
    map((result: any) => (result.member ?? result['hydra:member'] ?? []).map((x: any) => this.certificationNodeAdapter.adapt(x)))
  );
}

// Add a certification to the catalogue. Edges can be wired in the same call; both are optional.
createCertificationNode(orgId: string, certificationId: string, edges: { covers?: string[]; coveredBy?: string[] } = {}) {
  return this.certificationNodeApiService.create({
    organisationProfile: `/organisation_profiles/${orgId}`,
    certification: `/default_certifications/${certificationId}`,
    ...(edges.covers ? { covers: edges.covers } : {}),
    ...(edges.coveredBy ? { coveredBy: edges.coveredBy } : {}),
  });
}

// Replace a node's edges in one direction. Both fields are full-replace, not append — send the
// complete desired set; `[]` clears every edge in that direction. organisationProfile is ignored
// on PATCH, so a node can't be moved between organisations. Refetch the whole graph after saving:
// a PATCH on one node also rewrites the opposite array on every counterpart.
updateCertificationNodeEdges(id: string, edges: { covers?: string[]; coveredBy?: string[] }) {
  return this.certificationNodeApiService.update(id, edges);
}

// Removes the node and every edge touching it, in both directions. No bridging: if A → A.1 → Base
// and A.1 is deleted, A no longer grants Base.
deleteCertificationNode(id: string) {
  return this.certificationNodeApiService.delete(id);
}

// ----- Grant yearly maximums -----

// Every configured (grant, year) maximum for the organisation, unpaginated.
getGrantYearlyMaximums(orgId: string): Observable<GrantYearlyMaximum[]> {
  return this.organisationProfileApiService.getGrantYearlyMaximums(orgId).pipe(
    map((result: any) => (result.member ?? result['hydra:member'] ?? []).map((x: any) => this.grantYearlyMaximumAdapter.adapt(x)))
  );
}

// The grant × joint-committee matrix for one year: rows the org may use, each with its editable cells.
// IRIs are reduced to ids so the screen can key its controls and issue PATCH/DELETE/POST directly.
getGrantMaximumMatrix(orgId: string, year: number): Observable<GrantMaximumMatrix> {
  const lastSeg = (iri: any): string | null => iri == null ? null : String(iri).split('/').pop() ?? null;
  return this.organisationProfileApiService.getGrantYearlyMaximumMatrix(orgId, year).pipe(
    map((res: any): GrantMaximumMatrix => ({
      year: Number(res?.year ?? year),
      rows: (res?.grants ?? []).map((g: any) => ({
        grantId: lastSeg(g.grant) ?? '',
        grantName: g.grantName ?? '',
        companyWide: !!g.companyWide,
        cells: (g.cells ?? []).map((c: any) => ({
          committeeId: lastSeg(c.jointCommittee),
          committeeName: c.jointCommitteeName ?? null,
          maximumId: lastSeg(c.maximumId),
          maximumAmount: c.maximumAmount != null ? Number(c.maximumAmount) : null,
        })),
      })),
    }))
  );
}

// First time a (grant, jointCommittee, year) maximum is set. maximumAmount is sent as a string;
// jointCommitteeId null means a company-wide ceiling.
createGrantYearlyMaximum(orgId: string, grantId: string, jointCommitteeId: string | null, year: number, maximumAmount: number) {
  return this.grantYearlyMaximumApiService.create({
    organisationProfile: `/organisation_profiles/${orgId}`,
    grant: `/grants/${grantId}`,
    jointCommittee: jointCommitteeId ? `/joint_committees/${jointCommitteeId}` : null,
    year,
    maximumAmount: maximumAmount.toString(),
  });
}

// Edit an existing maximum (grant/year/organisation don't change).
updateGrantYearlyMaximum(id: string, maximumAmount: number) {
  return this.grantYearlyMaximumApiService.update(id, { maximumAmount: maximumAmount.toString() });
}

// Clearing a maximum removes it entirely (back to "no ceiling").
deleteGrantYearlyMaximum(id: string) {
  return this.grantYearlyMaximumApiService.delete(id);
}

// Grant analytics: per-grant maximum vs. approved spend, one dataset per year, grouped as requested.
// groupBy is only sent for a real dimension (team/department/jointCommittee); "year" means no grouping.
getGrantAnalytics(orgId: string, criteria: { startYear: number, endYear: number, groupBy: string }): Observable<any> {
  const params: Record<string, any> = { startYear: criteria.startYear, endYear: criteria.endYear };
  if (criteria.groupBy && criteria.groupBy !== 'year') params['groupBy'] = criteria.groupBy;
  return this.organisationProfileApiService.getGrantAnalytics(orgId, params);
}

getSocialBalanceEntries(orgId: string): Observable<any[]> {
  return this.socialBalanceEntryApiService.list({
    'organisationProfile.id': orgId,
    'paging': 'false'
  }).pipe(map((result: any) => result.members));
}

createSocialBalanceEntry(data: object) {
  return this.socialBalanceEntryApiService.create(data);
}

updateSocialBalanceEntry(id: string, data: object) {
  return this.socialBalanceEntryApiService.update(id, data);
}

getDefaultCertifications():Observable<HydraCollection<Certification>>{
  return this.certificationApiService.list();
}

private certificationsCache = new Map<string, Observable<{items:Certification[]}>>();

getCertifications(orgId:string, criteria:any = null):Observable<{items:Certification[]}>{
  if (!this.certificationsCache.has(orgId)) {
    this.certificationsCache.set(orgId,
      (this.organisationProfileApiService.getCertifications(orgId, {}) as Observable<{items:Certification[]}>).pipe(
        shareReplay(1)
      )
    );
  }
  return this.certificationsCache.get(orgId)!;
}

invalidateCertificationsCache(orgId?: string) {
  if (orgId) {
    this.certificationsCache.delete(orgId);
  } else {
    this.certificationsCache.clear();
  }
}

searchTeachers(orgId:string, search:string){
  return this.organisationProfileApiService.teacherSearch(orgId, search);
}
}
